import { useContext } from 'react';
import { RecaptchaContext } from '../context/RecaptchaContext.js';
import '@swc/helpers';

/**
 * Custom hook to access the reCAPTCHA context
 *
 * @throws {Error} When used outside of RecaptchaProvider
 * @returns {RecaptchaContextValue} The reCAPTCHA context value
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { execute, isReady, loadingState } = useRecaptcha()
 *
 *   const handleSubmit = async () => {
 *     if (!isReady) return
 *
 *     try {
 *       const token = await execute({ action: 'submit_form' })
 *       // Use token with your API call
 *     } catch (error) {
 *       console.error('reCAPTCHA execution failed:', error)
 *     }
 *   }
 *
 *   if (loadingState === 'loading') {
 *     return <div>Loading reCAPTCHA...</div>
 *   }
 *
 *   return (
 *     <button onClick={handleSubmit} disabled={!isReady}>
 *       Submit
 *     </button>
 *   )
 * }
 * ```
 */ function useRecaptcha() {
    const context = useContext(RecaptchaContext);
    if (!context) {
        throw new Error('useRecaptcha must be used within a RecaptchaProvider. ' + 'Make sure to wrap your component tree with <RecaptchaProvider>.');
    }
    return context;
}
/**
 * Safe version of useRecaptcha that returns null instead of throwing
 * Use this when the provider might not be available
 */ function useRecaptchaSafe() {
    const context = useContext(RecaptchaContext);
    return context;
}

export { useRecaptcha, useRecaptchaSafe };
//# sourceMappingURL=useRecaptcha.js.map
